import { useTranslation } from '@rhino-project/core/i18n';
import { Children, ReactNode, useCallback, useMemo, useState } from 'react';

import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useModelShowContext } from '@rhino-project/core/hooks';
import { getModelShowPath } from '@rhino-project/core/utils';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { getParentModel, isBaseOwned } from '@rhino-project/core/utils';
import { IconButton } from '../buttons';
import { ModelEditModal } from './ModelEditModal';
import { useBaseOwnerNavigation } from '../../hooks';

interface ModelShowActionProps {
  children?: ReactNode;
  [key: string]: unknown;
}

export const ModelShowActionEdit = ({
  children,
  ...props
}: ModelShowActionProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <IconButton
      as={Link}
      color="primary"
      icon="bi:pencil-square"
      to={`${location.pathname}/edit`}
      search={{ back: location.pathname }}
      {...props}
    >
      {children || t('rhino.frontend.messages.edit')}
    </IconButton>
  );
};

export const ModelShowActionEditModal = (props: ModelShowActionProps) => {
  const { model, resource } = useModelShowContext();
  const [modalOpen, setModalOpen] = useState(false);

  const handleModalClose = () => setModalOpen(false);
  const handleClick = useCallback(() => setModalOpen(true), [setModalOpen]);

  return (
    <>
      <ModelShowActionEdit onClick={handleClick} {...props} />
      <ModelEditModal
        model={model}
        modelId={resource.id}
        isOpen={modalOpen}
        onModalClose={handleModalClose}
      />
    </>
  );
};

export const ModelShowActionDelete = ({
  children,
  ...props
}: ModelShowActionProps) => {
  const { t } = useTranslation();
  const {
    model,
    resource,
    delete: { mutate }
  } = useModelShowContext();
  const navigate = useNavigate();
  const location = useLocation();
  const baseOwnerNavigation = useBaseOwnerNavigation();

  const handleClick = useCallback(() => {
    if (
      confirm(
        t('rhino.frontend.messages.do_you_want_to_delete_value', {
          value1: resource.display_name
        })
      )
    ) {
      mutate(resource.id, {
        onSuccess: () => {
          if (isBaseOwned(model)) {
            // Dynamic relative navigation; the runtime `from` path doesn't match
            // the router's typed route union.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            navigate({ from: location.pathname, to: '..' } as any);
          } else {
            const parentModel = getParentModel(model)!;
            const parent = resource[parentModel.model];
            baseOwnerNavigation.push(getModelShowPath(parentModel, parent.id));
          }
        }
      });
    }
  }, [
    t,
    resource,
    mutate,
    model,
    navigate,
    location.pathname,
    baseOwnerNavigation
  ]);

  return (
    <IconButton color="danger" icon="bi:trash" onClick={handleClick} {...props}>
      {children || t('rhino.frontend.messages.delete')}
    </IconButton>
  );
};

const defaultComponents = {
  ModelShowActionDelete,
  ModelShowActionEdit
};

export interface ModelShowActionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  actions?: ReactNode;
  append?: boolean;
  prepend?: boolean;
  [key: string]: unknown;
}

export const ModelShowActionsBase = ({
  overrides,
  actions,
  append = false,
  prepend = false,
  ...props
}: ModelShowActionsProps) => {
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelShowActionDelete, ModelShowActionEdit } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );
  const { resource } = useModelShowContext();

  const computedDefaultActions = useMemo(
    () =>
      [
        // eslint-disable-next-line @eslint-react/no-missing-key, @eslint-react/static-components
        resource?._permissions?.update && <ModelShowActionEdit />,
        // eslint-disable-next-line @eslint-react/no-missing-key, @eslint-react/static-components
        resource?._permissions?.destroy && <ModelShowActionDelete />
      ].filter(Boolean),
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps
    [resource?._permissions?.destroy, resource?._permissions?.update]
  );

  const computedActions = useMemo(() => {
    if (!actions) return computedDefaultActions;

    if (append)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...computedDefaultActions, ...Children.toArray(actions)];

    if (prepend)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...Children.toArray(actions), ...computedDefaultActions];

    return actions;
  }, [actions, append, prepend, computedDefaultActions]);

  return (
    <div className="flex flex-row flex-wrap justify-between mb-3" {...props}>
      {
        // eslint-disable-next-line @eslint-react/no-children-map
        Children.map(computedActions, (action) => action)
      }
    </div>
  );
};

const MODAL_EDIT_OVERRIDES = { ModelShowActionEdit: ModelShowActionEditModal };
export const ModelShowActionsModalEdit = (props: ModelShowActionsProps) => (
  <ModelShowActionsBase overrides={MODAL_EDIT_OVERRIDES} {...props} />
);

export const ModelShowActions = (props: ModelShowActionsProps) =>
  useGlobalComponentForModel('ModelShowActions', ModelShowActionsBase, props);
